import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Mail, X, LifeBuoy } from 'lucide-react';
import { community } from '../mock';

/**
 * Floating Support button. Opens a small popover with Telegram + Gmail links.
 * Usage:
 *   <SupportButton />  (defaults to bottom-right fixed)
 *   <SupportButton inline />  (renders inline, no fixed position)
 */
const SupportButton = ({ inline = false, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const tgUrl = community?.find?.((c) => /telegram/i.test(c.label))?.url || 'https://t.me/DomiaAirdropBot';
  const mailUrl = 'mailto:contact.dominoarena@gmail.com?subject=Support%20Domino%20Arena';

  const container = inline
    ? `relative ${className}`
    : `fixed bottom-5 right-5 z-[60] ${className}`;

  return (
    <div ref={ref} className={container}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="support-button"
        title="Support / Help"
        className="group flex items-center gap-2 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white pl-2 pr-3.5 py-2 shadow-[0_10px_24px_rgba(30,64,175,0.4)] hover:scale-105 transition-transform border border-blue-300/30"
      >
        <span className="w-7 h-7 rounded-full bg-white/15 ring-2 ring-white/30 flex items-center justify-center shrink-0">
          <LifeBuoy className="w-4 h-4 text-white" strokeWidth={2.4} />
        </span>
        <span className="text-[12px] font-extrabold tracking-wide">Support</span>
      </button>

      {open && (
        <div
          className={`absolute ${inline ? 'top-full mt-2 right-0' : 'bottom-full mb-3 right-0'} w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200`}
          data-testid="support-menu"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-blue-700 to-blue-900 text-white flex items-center justify-between">
            <span className="text-[12px] font-black tracking-widest uppercase">Need Help?</span>
            <button onClick={() => setOpen(false)} className="p-0.5 hover:bg-white/15 rounded">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-2 space-y-1">
            <a
              href={tgUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="support-telegram-link"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-[#229ED9] text-white flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <div className="text-[12px] font-black text-slate-900">Telegram</div>
                <div className="text-[10px] text-slate-500">Live chat & quick help</div>
              </div>
            </a>
            <a
              href={mailUrl}
              data-testid="support-email-link"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-amber-500 text-white flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <div className="text-[12px] font-black text-slate-900">Gmail</div>
                <div className="text-[10px] text-slate-500">Tech issues & complaints</div>
              </div>
            </a>
          </div>
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 text-[9px] font-bold text-slate-400 text-center tracking-widest uppercase">
            Domino Arena · 24/7 support
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportButton;
